import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { exportToCsv } from "../../lib/exportCsv";
import { Megaphone, Send } from "lucide-react";

const AUDIENCES = [
    { id: "all", label: "Everyone" },
    { id: "free", label: "Free tier" },
    { id: "adventure", label: "Adventure tier" },
    { id: "premium", label: "Premium tier" },
    { id: "admin", label: "Admins only" },
];

export default function NotificationsTab() {
    const [items, setItems] = useState([]);
    const [kinds, setKinds] = useState([]);
    const [kind, setKind] = useState("all");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const pageSize = 30;
    const [loading, setLoading] = useState(true);
    const [composer, setComposer] = useState({ title: "", body: "", link: "", audience: "all" });
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/notifications", {
                params: { kind, page, page_size: pageSize },
            });
            setItems(data.items || []);
            setKinds(data.kinds || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [kind, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [kind]);
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const send = async () => {
        if (!composer.title.trim() || composer.title.trim().length < 3) {
            toast.error("Title must be at least 3 characters");
            return;
        }
        if (!composer.body.trim()) {
            toast.error("Body cannot be empty");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post("/admin/notifications/broadcast", composer);
            toast.success(`Broadcasted to ${data.recipients} users`);
            setComposer({ title: "", body: "", link: "", audience: "all" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    const onExport = () =>
        exportToCsv(
            items,
            [
                { key: "id", label: "ID" },
                { key: "user_id", label: "User ID" },
                { key: "kind", label: "Kind" },
                { key: "title", label: "Title" },
                { key: "body", label: "Body" },
                { key: "read", label: "Read", map: (r) => (r.read ? "yes" : "") },
                { key: "created_at", label: "Created" },
            ],
            "notifications"
        );

    return (
        <div className="space-y-4">
            <Composer composer={composer} setComposer={setComposer} send={send} sending={sending} />

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex gap-1 bg-snowcap border border-ridge-line rounded-lg p-1 flex-wrap">
                    <button
                        data-testid="notif-kind-all"
                        onClick={() => setKind("all")}
                        className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest ${
                            kind === "all" ? "bg-midnight-pine text-white" : "text-slate-gray hover:text-granite"
                        }`}
                    >
                        All
                    </button>
                    {kinds.map((k) => (
                        <button
                            key={k}
                            data-testid={`notif-kind-${k}`}
                            onClick={() => setKind(k)}
                            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest ${
                                kind === k ? "bg-midnight-pine text-white" : "text-slate-gray hover:text-granite"
                            }`}
                        >
                            {k}
                        </button>
                    ))}
                </div>
                <button
                    data-testid="notif-export"
                    onClick={onExport}
                    disabled={items.length === 0}
                    className="px-3 py-1.5 rounded-md bg-midnight-pine text-white text-xs font-bold hover:bg-deep-evergreen disabled:opacity-40"
                >
                    Export CSV
                </button>
            </div>

            <div className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-alpine-mist">
                        <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-gray">
                            <th className="py-3 px-4">Kind</th>
                            <th className="py-3 px-4">Title</th>
                            <th className="py-3 px-4">Recipient</th>
                            <th className="py-3 px-4">Read</th>
                            <th className="py-3 px-4">When</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="py-10 text-center text-slate-gray">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={5} className="py-10 text-center text-slate-gray">No notifications to show.</td></tr>
                        ) : items.map((n) => (
                            <tr key={n.id} data-testid={`notif-row-${n.id}`} className="border-t border-ridge-line">
                                <td className="py-3 px-4 text-xs">
                                    <span className="capitalize px-2 py-0.5 rounded-full text-[10px] font-bold bg-alpine-mist text-granite">
                                        {n.kind}
                                    </span>
                                </td>
                                <td className="py-3 px-4 text-granite font-semibold truncate max-w-[280px]">{n.title}</td>
                                <td className="py-3 px-4 text-xs text-slate-gray font-mono">{n.user_id}</td>
                                <td className="py-3 px-4 text-xs">{n.read ? "✓" : "—"}</td>
                                <td className="py-3 px-4 text-xs text-slate-gray">{n.created_at ? new Date(n.created_at).toLocaleString() : "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="flex items-center justify-between p-4 border-t border-ridge-line">
                    <span className="text-xs text-slate-gray font-semibold">{total} total</span>
                    <div className="flex gap-2">
                        <button
                            data-testid="notif-prev"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40"
                        >
                            Prev
                        </button>
                        <span className="text-xs font-bold text-granite self-center">{page} / {totalPages}</span>
                        <button
                            data-testid="notif-next"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Composer({ composer, setComposer, send, sending }) {
    const set = (k, v) => setComposer((c) => ({ ...c, [k]: v }));
    return (
        <div
            data-testid="notif-composer"
            className="bg-snowcap border border-ridge-line rounded-lg p-5"
        >
            <div className="flex items-center gap-2 mb-4">
                <Megaphone className="w-5 h-5 text-ember-orange" />
                <h3 className="font-display font-bold text-lg text-midnight-pine">Broadcast announcement</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <Field label="Title">
                    <input
                        data-testid="notif-title"
                        value={composer.title}
                        onChange={(e) => set("title", e.target.value)}
                        className={inputCls}
                        placeholder="e.g. New Passport stamps are live"
                    />
                </Field>
                <Field label="Audience">
                    <select
                        data-testid="notif-audience"
                        value={composer.audience}
                        onChange={(e) => set("audience", e.target.value)}
                        className={inputCls}
                    >
                        {AUDIENCES.map((a) => (
                            <option key={a.id} value={a.id}>{a.label}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Body" full>
                    <textarea
                        data-testid="notif-body"
                        rows={3}
                        value={composer.body}
                        onChange={(e) => set("body", e.target.value)}
                        className={inputCls}
                        placeholder="What's happening…"
                    />
                </Field>
                <Field label="Link (optional)" full>
                    <input
                        data-testid="notif-link"
                        value={composer.link}
                        onChange={(e) => set("link", e.target.value)}
                        className={inputCls}
                        placeholder="/passport"
                    />
                </Field>
            </div>
            <div className="flex justify-end mt-4">
                <button
                    data-testid="notif-send-btn"
                    onClick={send}
                    disabled={sending}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ember-orange text-white text-sm font-bold hover:bg-ember-dark disabled:opacity-50"
                >
                    <Send className="w-4 h-4" /> {sending ? "Sending…" : "Broadcast"}
                </button>
            </div>
        </div>
    );
}

const inputCls =
    "w-full px-3 py-2 rounded-md bg-alpine-mist border border-ridge-line text-sm focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20";

function Field({ label, children, full }) {
    return (
        <label className={`block ${full ? "md:col-span-2" : ""}`}>
            <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-1.5">{label}</span>
            {children}
        </label>
    );
}
